import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    CONTROL_TYPE_ID_THERMOSTAT,
    ControlModel,
    ControlTypeModel,
    ThermostatImageSet,
    CONTROL_TYPE_ADV_THERMOSTAT,
    BRIDGE_DEVICE_TYPE_ID_KNX
} from '../../../types/control';
import {useRPC} from '../../../hooks/rpc';
import {TemperatureControlComponent} from './component';
import {useSelector} from 'react-redux';
import {useDebounce} from '../../../hooks/debounce';
import {useFilterSelector} from '../../../hooks/filter-selector';
import {useQuestion} from '../../../hooks/question';
import {useLocalization} from "../../../hooks/localization";
import { useMessage } from '../../../hooks/message';

type Props = {
    control: ControlModel;
};

export const TemperatureControl = memo(function TemperatureControl({control}: Props) {
    const thermostatControlTypeList = useFilterSelector(
        state => state.control.controlTypeList,
        controlType => controlType.id === (CONTROL_TYPE_ID_THERMOSTAT || CONTROL_TYPE_ADV_THERMOSTAT),
        []);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const homeValueTemperature = useMemo(() => {
        for (const bridge_device of bridgeDeviceList) {
            //if (bridge_device.id === control.temperature_settings.bridge_device_id) {
            if (control.type_id === CONTROL_TYPE_ADV_THERMOSTAT) {
                return [control.temperature_settings.room_whole, control.temperature_settings.room_fraction]
            } else if (bridge_device.id === control.temperature_settings.bridge_device_id && bridge_device.type_id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                return [bridge_device.room_temperature_whole, bridge_device.room_temperature_fraction]
            } else if (bridge_device.type_id === BRIDGE_DEVICE_TYPE_ID_KNX) {
                const knxWhole = control.parameters["thermostat_room_temp_whole"];
                const knxFraction = control.parameters["thermostat_room_temp_fraction"]
                if (knxWhole === undefined || knxFraction === undefined) {
                    return [0, 0]
                }
                return [knxWhole, knxFraction]
            }
            //}
        }
        return [0, 0]
    }, [bridgeDeviceList, control])

    const [rpc] = useRPC();
    const {failure} = useMessage()
    const updateTemperature = useDebounce((id: string, whole: number, fraction: number) => {
        rpc.updateTemperature(id, whole, fraction).catch(failure)
    }, 200)
    const [whole, setWhole] = useState(control.temperature_settings.whole || 14);
    const [fraction, setFraction] = useState(whole === 14 ? 5 : control.temperature_settings.fraction || 0);
    const [currentTemperature, setCurrentTemperature] = useState(homeValueTemperature)
    const timeoutRef = useRef<number>();
    const controlRef = useRef<ControlModel>();
    const question = useQuestion();
    const {t} = useLocalization();
    controlRef.current = control;

    useEffect(() => {
        // @ts-ignore
        timeoutRef.current = setTimeout(() => {
            if (whole !== controlRef.current.temperature_settings.whole) {
                setWhole(controlRef.current.temperature_settings.whole)
            }
            if (fraction !== controlRef.current.temperature_settings.fraction) {
                setFraction(controlRef.current.temperature_settings.fraction)
            }
            if (currentTemperature !== homeValueTemperature) {
                setCurrentTemperature(homeValueTemperature)
            }
            timeoutRef.current = null
        }, 1000)

        return () => clearTimeout(timeoutRef.current)
        // eslint-disable-next-line
    }, [fraction, whole, homeValueTemperature, currentTemperature])

    useEffect(() => {
        if (timeoutRef.current == null) {
            setWhole(control.temperature_settings.whole);
            setFraction(control.temperature_settings.fraction);
            setCurrentTemperature(homeValueTemperature);
        }
    }, [control, homeValueTemperature]);

    const temperatureClick = useCallback((isPlus: boolean) => {
        let newWhole = whole;
        let newFraction = fraction;
        if (isPlus) {
            newFraction += 5;
            if (newFraction >= 10) {
                newFraction = 0;
                newWhole++;
            }
        } else {
            newFraction -= 5;
            if (newFraction < 0) {
                newFraction = 5;
                newWhole--;
            }
        }

        if (newWhole < 15) {
            newWhole = 14;
            newFraction = 5;
        } else if (newWhole > 32) {
            newWhole = 33;
            newFraction = 0;
        }

        setWhole(newWhole);
        setFraction(newFraction);
        updateTemperature(control.id, newWhole, newFraction);
    }, [control.id, fraction, updateTemperature, whole]);

    // const burning = useMemo(() => {
    //     if (whole > homeValueTemperature[0]) {
    //         return true;
    //     }
    //
    //     if (whole === homeValueTemperature[0]) {
    //         return fraction > homeValueTemperature[1];
    //     }
    //
    //     return false;
    // }, [fraction, whole, homeValueTemperature]);

    const icon: string = useMemo(function () {
        if (thermostatControlTypeList.length === 0) {
            return null;
        }

        const controlType = thermostatControlTypeList[0];
        const imageSet = (controlType as ControlTypeModel<ThermostatImageSet>).image_set;
        if (control.current_value === 1) {
            return control.temperature_settings.is_mode_heating ? imageSet.heat_on : imageSet.cold_on;
        } else {
            return control.temperature_settings.is_mode_heating ? imageSet.heat_off : imageSet.cold_off;
        }
    }, [control.current_value, control.temperature_settings.is_mode_heating, thermostatControlTypeList]);
    const click = useCallback(() => {
        if (control.temperature_settings.has_heating && control.temperature_settings.has_cooling) {
            //yakut
            question(t('pls_heating_mode'), () => {
                rpc.changeHeatingMode(control.id).catch(failure);
            });
        }
    }, [rpc, control, t, question, failure]);


    return (
        <TemperatureControlComponent
            control={control}
            icon={icon}
            click={click}
            temperatureClick={temperatureClick}
            homeValueTemperature={currentTemperature[0] + '.' + currentTemperature[1]}
            valueTemperature={whole + '.' + fraction}/>
    );
});
